if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');
      if (!this.elements.thumbnails) return;

      // Enable continuous slider looping so SliderComponent doesn't disable buttons
      this.elements.thumbnails.enableSliderLooping = true;
      if (this.elements.viewer) {
        this.elements.viewer.enableSliderLooping = true;
      }

      this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

      // Thumbnail click listener
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button')?.addEventListener('click', (e) => {
          e.preventDefault();
          this.setActiveMedia(mediaToSwitch.dataset.target, false);
        });
      });

      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

      // Navigation arrow buttons: prev & next
      const prevBtn = this.elements.thumbnails.querySelector('button[name="previous"]');
      const nextBtn = this.elements.thumbnails.querySelector('button[name="next"]');

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();

          const allThumbs = this.getVisibleThumbnails();
          if (allThumbs.length <= 1) return;

          const currentThumb = this.elements.thumbnails.querySelector('[aria-current="true"]')?.closest('li[data-target]') || allThumbs[0];
          let currentIndex = allThumbs.indexOf(currentThumb);
          if (currentIndex === -1) currentIndex = 0;

          const prevIndex = (currentIndex - 1 + allThumbs.length) % allThumbs.length;
          this.setActiveMedia(allThumbs[prevIndex].dataset.target, false);
          this.updateSliderButtonsState(allThumbs.length);
        }, true);
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();

          const allThumbs = this.getVisibleThumbnails();
          if (allThumbs.length <= 1) return;

          const currentThumb = this.elements.thumbnails.querySelector('[aria-current="true"]')?.closest('li[data-target]') || allThumbs[0];
          let currentIndex = allThumbs.indexOf(currentThumb);
          if (currentIndex === -1) currentIndex = 0;

          const nextIndex = (currentIndex + 1) % allThumbs.length;
          this.setActiveMedia(allThumbs[nextIndex].dataset.target, false);
          this.updateSliderButtonsState(allThumbs.length);
        }, true);
      }

      // Keep buttons enabled on scroll
      this.elements.thumbnails.addEventListener('scroll', () => {
        this.updateSliderButtonsState();
      }, { passive: true });

      // Initial check on buttons
      this.updateSliderButtonsState();
    }

    getVisibleThumbnails() {
      if (!this.elements.thumbnails) return [];
      const all = Array.from(this.elements.thumbnails.querySelectorAll('li[data-target]'));
      return all.filter(thumb => {
        if (thumb.classList.contains('thumbnail-list_item--variant')) return false;
        const targetId = thumb.dataset.target;
        if (!targetId) return false;
        const mediaEl = this.elements.viewer?.querySelector(`[data-media-id="${targetId}"]`);
        if (!mediaEl) return false;
        if (mediaEl.classList.contains('product__media-item--variant')) return false;
        return true;
      });
    }

    updateSliderButtonsState(count) {
      if (!this.elements.thumbnails) return;
      const total = count !== undefined ? count : this.getVisibleThumbnails().length;
      const prevBtn = this.elements.thumbnails.querySelector('button[name="previous"]');
      const nextBtn = this.elements.thumbnails.querySelector('button[name="next"]');

      if (total <= 1) {
        if (prevBtn) prevBtn.style.display = 'none';
        if (nextBtn) nextBtn.style.display = 'none';
      } else {
        if (prevBtn) {
          prevBtn.style.display = '';
          prevBtn.removeAttribute('disabled');
        }
        if (nextBtn) {
          nextBtn.style.display = '';
          nextBtn.removeAttribute('disabled');
        }
      }
    }

    onSlideChanged(event) {
      if (!event.detail || !event.detail.currentElement) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      if (!mediaId || !this.elements.viewer) return;

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) {
        console.warn('activeMedia not found for mediaId:', mediaId);
        return;
      }

      // Safe update: only remove active class once we know activeMedia exists
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement?.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail?.parentElement?.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails && activeMedia.parentElement) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      if (activeThumbnail) {
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      const btn = thumbnail.querySelector('button');
      if (btn) btn.setAttribute('aria-current', 'true');

      // Smoothly center the active thumbnail in view
      const slider = this.elements.thumbnails.slider;
      if (slider) {
        const thumbLeft = thumbnail.offsetLeft;
        const thumbWidth = thumbnail.clientWidth;
        const sliderWidth = slider.clientWidth;
        const targetScroll = thumbLeft - (sliderWidth / 2) + (thumbWidth / 2);
        slider.scrollTo({
          left: Math.max(0, targetScroll),
          behavior: 'smooth'
        });
      }

      this.updateSliderButtonsState();
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia?.();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
